import React, { useEffect, useState } from 'react';
import { Container, Card, Table, Button, Alert, Badge, Spinner, Form } from 'react-bootstrap';
import { useGetFscSyncQuery, useTriggerFscSyncMutation } from '../app/api';

// Super Admin -> FSC Sync.
//
// The Salesforce → Hub household sync used to be CLI-only, so any bulk CRM
// change meant opening a shell. This is the button, plus the run history that
// answers "did it actually work?".
//
// Dry run is the default: it reads Salesforce and reports what it *would*
// write. The safe press should be the easy one.

const STATUS = {
  queued:  { bg: 'secondary', label: 'Queued' },
  running: { bg: 'info',      label: 'Running' },
  success: { bg: 'success',   label: 'Success' },
  error:   { bg: 'danger',    label: 'Failed' },
};

function when(ts) {
  if (!ts) return '—';
  const d = new Date(ts.endsWith?.('Z') ? ts : `${ts}Z`);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
}

export default function FscSync() {
  const { data, isLoading, refetch } = useGetFscSyncQuery();
  const [trigger, { isLoading: isTriggering }] = useTriggerFscSyncMutation();
  const [live, setLive] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const active = data?.active;
  const runs = data?.runs || [];
  const cache = data?.cache || {};

  // Poll only while something is in flight — a sync takes minutes, and a page
  // left open shouldn't hammer the API for the rest of the day.
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(refetch, 4000);
    return () => clearInterval(t);
  }, [active, refetch]);

  const go = async () => {
    setError('');
    setNote('');
    try {
      const res = await trigger({ live }).unwrap();
      setNote(res.live
        ? 'Live sync started — the cache will be updated.'
        : 'Dry run started — nothing will be written.');
      await refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Could not start the sync.');
    }
  };

  if (isLoading) {
    return <Container className="py-4 text-center"><Spinner animation="border" /></Container>;
  }

  return (
    <Container className="py-4">
      <h3 className="mb-1">FSC Sync</h3>
      <p className="text-muted" style={{ maxWidth: '46rem' }}>
        Refreshes the Hub’s copy of Salesforce — households, offices, and the
        links that tie catalogued documents to a client. This is the cache behind
        Work → FSC Clients and document search. One-way: Salesforce stays the
        system of record, and nothing here writes back to it.
      </p>

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex gap-4 flex-wrap mb-3" style={{ fontSize: '0.88rem' }}>
            <div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>Households cached</div>
              <strong style={{ fontSize: '1.1rem' }}>
                {(cache.households ?? 0).toLocaleString()}
              </strong>
            </div>
            <div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>Documents catalogued</div>
              <strong style={{ fontSize: '1.1rem' }}>
                {(cache.catalog_documents ?? 0).toLocaleString()}
              </strong>
            </div>
            <div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>Document links</div>
              <strong style={{ fontSize: '1.1rem' }}>
                {(cache.document_links ?? 0).toLocaleString()}
              </strong>
            </div>
            <div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>Last synced</div>
              <strong>{when(cache.last_synced)}</strong>
            </div>
          </div>

          {/* Documents reach a client only through the links, so a client page
              showing no documents is a missing-link problem, not a sync problem —
              and the links can only exist for documents that are catalogued. Both
              gaps are called out here because neither is visible anywhere else. */}
          {cache.catalog_documents === 0 ? (
            <Alert variant="warning" className="py-2" style={{ fontSize: '0.82rem' }}>
              <strong>No documents are catalogued in this environment.</strong> Client
              pages will show no documents, and a sync cannot fix it: catalogue rows
              are copied between environments (their <code>doc_id</code> is the id
              Salesforce already stores), not rebuilt from Salesforce.
            </Alert>
          ) : cache.document_links === 0 && (
            <Alert variant="warning" className="py-2" style={{ fontSize: '0.82rem' }}>
              <strong>No document links in this environment.</strong> Documents carry
              Salentica account ids and clients are Salesforce ids, so without links
              every client shows zero documents. A sync below rebuilds them.
            </Alert>
          )}

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          {note && <Alert variant="success" dismissible onClose={() => setNote('')}>{note}</Alert>}

          {active ? (
            <Alert variant="info" className="d-flex align-items-center gap-2 mb-0">
              <Spinner animation="border" size="sm" />
              <div>
                <strong>{active.live ? 'Live sync' : 'Dry run'} in progress</strong>
                {' — '}{active.progress || 'starting…'}
                <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                  Started by {active.started_by} at {when(active.started)}
                </div>
              </div>
            </Alert>
          ) : (
            <>
              <Form.Check
                type="switch"
                id="fsc-live"
                className="mb-2"
                checked={live}
                onChange={e => setLive(e.target.checked)}
                label={
                  <>
                    <strong>Write changes</strong>
                    <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                      {' '}— off means a dry run: reads Salesforce and reports what it
                      would change, without touching the cache.
                    </span>
                  </>
                }
              />
              <Button variant={live ? 'primary' : 'outline-primary'} onClick={go} disabled={isTriggering}>
                {isTriggering ? <Spinner animation="border" size="sm" /> : <i className="bi bi-arrow-repeat" />}
                {' '}{live ? 'Sync from Salesforce' : 'Run a dry run'}
              </Button>
              <div className="text-muted mt-2" style={{ fontSize: '0.78rem' }}>
                Reads about 20,000 Salesforce records; takes a few minutes. Safe to
                repeat — it updates existing rows rather than duplicating them.
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <h5 className="mb-2">Recent runs</h5>
      <Card>
        <Table responsive hover className="mb-0 align-middle">
          <thead>
            <tr>
              <th style={{ width: '10%' }}>Status</th>
              <th style={{ width: '9%' }}>Mode</th>
              <th style={{ width: '20%' }}>Started</th>
              <th style={{ width: '21%' }}>By</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {runs.map(r => {
              const s = STATUS[r.status] || { bg: 'secondary', label: r.status };
              const d = r.detail;
              return (
                <tr key={r.id}>
                  <td><Badge bg={s.bg}>{s.label}</Badge></td>
                  <td style={{ fontSize: '0.82rem' }}>{r.live ? 'Live' : 'Dry run'}</td>
                  <td style={{ fontSize: '0.82rem' }}>{when(r.started)}</td>
                  <td className="text-muted" style={{ fontSize: '0.78rem' }}>{r.started_by}</td>
                  <td style={{ fontSize: '0.8rem' }}>
                    {typeof d === 'object' && d !== null ? (
                      <>
                        {d.households?.toLocaleString()} household(s), {d.offices} office(s)
                        {d.doc_links != null && <>, {d.doc_links.toLocaleString()} doc link(s)</>}
                        {/* `live` arrives as MySQL's 0/1, and `0 && …` renders a
                            literal 0 — hence the Boolean(). */}
                        {Boolean(r.live) && d.written != null && <> · <strong>{d.written.toLocaleString()} written</strong></>}
                        {!r.live && <span className="text-muted"> · nothing written</span>}
                      </>
                    ) : (
                      <span className={r.status === 'error' ? 'text-danger' : 'text-muted'}>
                        {d || r.progress || '—'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {runs.length === 0 && (
              <tr><td colSpan={5} className="text-center text-muted py-4">
                No syncs recorded yet.
              </td></tr>
            )}
          </tbody>
        </Table>
      </Card>
    </Container>
  );
}
